import { assertUnreachable, isDefined } from '@sgme/fp';
import type { ApiErrorMessage } from 'epic/apiErrorEpicHelper';
import { DocumentationV3 } from 'epic/common-form/Documentation';
import { AddonAction } from 'models/AddonAction';
import { DocumentType } from 'models/documents/DocumentType';
import type { FormError } from 'models/formError';
import type { ListFieldMetadata } from 'models/forms/fields/ListFieldMetadata';
import type { ISalesCounterpart } from 'models/ISalesCounterpart';
import type { Underlying } from 'models/underlying';
import type { TradeClientSide } from 'state/addonForm';
import { type QuoteFormError } from 'state/addonForm/addonForm.selector';
import { getBatchFormQuoteId, getBatchQuoteDetails } from 'state/batchForm/batchForm.selectors';
import { getDynamicFormIsEnrichmentError } from 'state/dynamic/infos/infos.selectors';
import { getDynamicFormPriceEnhancementMode, getDynamicWorkflowStatus } from 'state/dynamic/selectors';
import { selectEssentialFormInfos, selectEssentialsFormQuoteId } from 'state/essential/infos/selectors';
import type { DataById } from 'state/formData/formData.model';
import { getFormEnrichedBackToBackType, getFormEnrichedData } from 'state/formData/formData.selectors';
import { getFieldsMetadata } from 'state/forms/metadata/selectors';
import { getRejections } from 'state/forms/rejections/selectors';
import { getAllQuoteIdsUsedInDashboard } from 'state/quotes/dashboard/selectors';
import { type GreekLine, type PricerId, type QuoteDetailsData } from 'state/quotes/details/details.model';
import { selectQuoteDetails } from 'state/quotes/details/selectors';
import type { QuoteType } from 'state/quotes/infos/infos.model';
import * as fromPrices from 'state/quotes/priceEnhancements/priceEnhancements.selectors';
import * as fromQuoteResult from 'state/quotes/results/results.selectors';
import { getSelectedCorrelationGraph } from 'state/quotes/selectedCorrelationGraph/selectedCorrelationGraph.selectors';
import { getQuoteType } from 'state/quotes/selectors';
import * as fromSponsoFees from 'state/quotes/sponsoFees/sponsoFees.selectors';
import { getStreamingDetails } from 'state/quotes/streaming/streaming.selector';
import * as fromTrade from 'state/quotes/trades/trades.selectors';
import { isLiveQuote } from 'state/quotes/utils';
import type { RoutePath } from 'state/router';
import * as fromRouter from 'state/router/router.selectors';
import {
  validateClientTradeBackToBackFields,
  validateHedgingTradeBackToBackFields,
} from 'state/selectors/common/commonForm.helper';
import { getUserCompanyBdrId } from 'state/selectors/common/user';
import { addonSelector, getFormRequestType } from 'state/selectors/selectors';
import type { AppState } from 'state/state.model';
import { getStaticFormIsEnrichmentError } from 'state/static/infos/infos.selectors';
import { getStaticFormPriceEnhancementMode, getStaticWorkflowStatus } from 'state/static/selectors';
import type { TradeConfirmationBackToBackFields } from 'state/uiPanelState/tradeConfirmation/tradeConfirm.model';
import * as fromTradeConfirmation from 'state/uiPanelState/tradeConfirmation/tradeConfirm.selectors';
import type { QuoteWorkspaceType, WorkspaceType } from 'state/workspaces/model';
import { now } from 'utils/core/now';
import { getDateOnlyFromDate, getDateOnlyFromIsoString } from 'utils/date';

import { getBatchAutoSolve } from './batchForm';
import {
  getDynamicFormInfos,
  getDynamicFormQuoteId,
  getDynamicQuoteDetails,
  getDynamicQuoteRequestStartingTimeInMillis,
  getDynamicQuoteState,
  getDynamicTabIdsFromQuoteId,
  getDynamicTechnicalError,
} from './dynamicForm';
import {
  getStaticFormInfos,
  getStaticFormQuoteId,
  getStaticQuoteDetails,
  getStaticQuoteRequestStartingTimeInMillis,
  getStaticQuoteState,
  getStaticTabIdsFromQuoteId,
  getStaticTechnicalError,
} from './staticForm';

export function getCurrentRoute(state: AppState): RoutePath {
  return fromRouter.getCurrentRoute(state.router);
}
export function getWorkspaceTypeFromRouter(state: AppState): WorkspaceType {
  const routeName = fromRouter.getCurrentRoute(state.router);

  switch (routeName) {
    case '/':
    case '/static':
      return 'static';

    case '/dynamic':
      return 'dynamic';

    case '/post-trade':
      return 'postTrade';

    case '/secondary-market-quotes-workspace':
      return 'secondaryMarketQuotes';

    case '/secondary-market-post-event-workspace':
      return 'secondaryMarketPostEvents';

    case '/batch':
      return 'newBatch';

    case '/essential':
      return 'essential';

    default:
      return 'static';
  }
}

export function getCurrentTabIdFromRouter(state: AppState): string | undefined {
  const routeName = fromRouter.getCurrentRoute(state.router);

  switch (routeName) {
    case '/':
    case '/static':
      return state.workspaces?.workspaces?.static?.activeTab;

    case '/dynamic':
      return state.workspaces?.workspaces?.dynamic?.activeTab;

    case '/quote-blotter':
      return state.workspaces?.workspaces?.ordersBasket?.activeTab;

    case '/post-trade':
      return state.workspaces?.workspaces?.postTrade?.activeTab;

    case '/secondary-market-quotes-workspace':
      return state.workspaces?.workspaces?.secondaryMarketQuotes?.activeTab;

    case '/secondary-market-post-event-workspace':
      return state.workspaces?.workspaces?.secondaryMarketPostEvents?.activeTab;

    case '/batch':
      return state.workspaces?.workspaces?.newBatch?.activeTab;

    case '/essential':
      return state.workspaces?.workspaces?.essential?.activeTab;

    default:
      return undefined;
  }
}
export function getFormInfos(state: AppState, workspace: QuoteWorkspaceType, tabId: string) {
  switch (workspace) {
    case 'static':
      return getStaticFormInfos(state, tabId);
    case 'essential':
      return selectEssentialFormInfos(state, tabId);
    default:
      return getDynamicFormInfos(state, tabId);
  }
}
export function getFormInfosDocumentation(
  state: AppState,
  workspace: QuoteWorkspaceType,
  tabId: string,
): DocumentationV3 | undefined {
  return getFormInfos(state, workspace, tabId)?.documentation;
}

export function getAvailableDocuments(state: AppState, workspace: QuoteWorkspaceType, tabId: string): DocumentType[] {
  return getFormInfosDocumentation(state, workspace, tabId)?.docs.map(doc => doc.type) ?? [];
}

export function getDocument(state: AppState, workspace: QuoteWorkspaceType, tabId: string, documentType: DocumentType) {
  const documentation = getFormInfosDocumentation(state, workspace, tabId);
  return documentation?.docs.find(document => document.type === documentType);
}

export function getFormQuoteState(state: AppState, workspace: QuoteWorkspaceType, tabId: string) {
  return workspace === 'static' ? getStaticQuoteState(state, tabId) : getDynamicQuoteState(state, tabId);
}
export function getTabIdsByQuoteId(state: AppState, quoteId: string) {
  const staticTabIds = getStaticTabIdsFromQuoteId(state, quoteId);
  const dynamicTabIds = getDynamicTabIdsFromQuoteId(state, quoteId);

  return [...staticTabIds, ...dynamicTabIds];
}

export function getFormQuoteRequestStartingTimeInMillis(state: AppState, workspace: QuoteWorkspaceType, tabId: string) {
  return workspace === 'static'
    ? getStaticQuoteRequestStartingTimeInMillis(state, tabId)
    : getDynamicQuoteRequestStartingTimeInMillis(state, tabId);
}

export function getTechnicalError(
  state: AppState,
  workspace: QuoteWorkspaceType,
  tabId: string,
  isForBooking: boolean = false,
): ApiErrorMessage | undefined {
  return workspace === 'static'
    ? getStaticTechnicalError(state, tabId)
    : getDynamicTechnicalError(state, tabId, isForBooking);
}

export function getInitialPriceEnhancement(state: AppState, workspace: QuoteWorkspaceType, tabId: string) {
  return getFormQuoteResult(state, workspace, tabId)?.priceEnhancement;
}

export function getAddonFormQuoteId(state: AppState, tabId: string): string | undefined {
  return state.addonForm[tabId]?.form?.lastQuoteId;
}

export function getAddonFormTradeClientSide(state: AppState, tabId: string): TradeClientSide | undefined {
  return state.addonForm[tabId]?.form?.tradeClientSide;
}

export function getAddonFormAddonAction(state: AppState, tabId: string): AddonAction | undefined {
  return state.addonForm[tabId]?.form?.addonAction;
}
export function getFormQuoteType(state: AppState, workspaceType: WorkspaceType, tabId: string) {
  const quoteId = getFormQuoteId(state, workspaceType, tabId);
  return getQuoteType(state, quoteId);
}
export function getFormQuoteId(state: AppState, workspace: WorkspaceType, tabId: string) {
  switch (workspace) {
    case 'static':
      return getStaticFormQuoteId(state, tabId);

    case 'postTrade':
      return getAddonFormQuoteId(state, tabId);

    case 'newBatch':
      return getBatchFormQuoteId(state, tabId);

    case 'dynamic':
      return getDynamicFormQuoteId(state, tabId);

    case 'essential':
      return selectEssentialsFormQuoteId(state, tabId);

    case 'addonManagement':
    case 'ordersBasket':
    case 'secondaryMarketPostEvents':
    case 'secondaryMarketQuotes':
    case 'smartQuote':
      return undefined;

    default:
      return assertUnreachable(workspace, 'Unhandled workspace type');
  }
}

export function isQuoteAcked(state: AppState, workspace: WorkspaceType, tabId: string): boolean {
  return getFormQuoteId(state, workspace, tabId) !== undefined;
}

export function getTradeInfosByQuoteId(state: AppState, quoteId: string | undefined) {
  return fromTrade.getTradeInfos(state.quotes.trades, quoteId);
}
export function getUserPriceEnhancementByQuoteId(state: AppState, quoteId: string | undefined) {
  const result = fromPrices.getPriceEnhancement(state.quotes.priceEnhancements, quoteId);
  if (result === undefined) {
    return undefined;
  }
  const { priceEnhancement } = fromQuoteResult.getQuoteResult(state.quotes.results, quoteId)!;
  return { ...priceEnhancement!, ...result };
}
export function getUserSponsoFeesByQuoteId(state: AppState, quoteId: string | undefined) {
  const result = fromSponsoFees.getSponsoFees(state.quotes.sponsoFees, quoteId);
  if (result === undefined) {
    return undefined;
  }
  return result;
}
export function getSelectedCorrelationGraphByTabId(state: AppState, tabId: string | undefined) {
  return getSelectedCorrelationGraph(state.quotes.selectedCorrelationGraph, tabId);
}

export function getTradeConfirmation(state: AppState, workspace: QuoteWorkspaceType, tabId: string) {
  const quoteId = getFormQuoteId(state, workspace, tabId);
  return fromTradeConfirmation.getTradeConfirmation(state.uiPanel.tradeConfirmations, quoteId);
}

export function isQuoteLiveTrading(state: AppState, workspaceType: 'static' | 'dynamic', tabId: string) {
  const quoteState = getFormQuoteState(state, workspaceType, tabId);
  const confirmation = getTradeConfirmation(state, workspaceType, tabId);
  const isLive = isLiveQuote(quoteState);
  return (isLive && confirmation?.isTrading) ?? false;
}
export function isQuoteLiveTraded(state: AppState, workspaceType: 'static' | 'dynamic', tabId: string) {
  const quoteState = getFormQuoteState(state, workspaceType, tabId);
  const confirmation = getTradeConfirmation(state, workspaceType, tabId);
  const isLive = isLiveQuote(quoteState);
  return (isLive && confirmation?.hadTraded) ?? false;
}

export function canTradeBackToBack(state: AppState, workspace: QuoteWorkspaceType, tabId: string) {
  const validationErrors = getBackToBackValidationErrors(state, workspace, tabId);
  return validationErrors.length === 0;
}

export function getBackToBackValidationErrors(
  state: AppState,
  workspace: QuoteWorkspaceType,
  tabId: string,
): FormError<TradeConfirmationBackToBackFields>[] {
  const { isBackToBack, backToBackType, backedNotional, fileCode, noteNominal, isDeltaAdjusted, isDelayedBackToBack } =
    getTradeConfirmation(state, workspace, tabId);

  if (!isBackToBack?.value) {
    return [];
  }
  const effectiveBackToBackType = backToBackType?.value ?? getFormEnrichedBackToBackType(state, tabId);

  if (!effectiveBackToBackType || effectiveBackToBackType === 'None') {
    return [];
  }

  switch (effectiveBackToBackType) {
    case 'ClientTrade': {
      const notional = getQuoteNotional(state, workspace, tabId) ?? 0;
      return validateClientTradeBackToBackFields(backedNotional, notional, isDelayedBackToBack);
    }
    case 'HedgingTrade': {
      const isDeltaAdjustedValue = isDefined(isDeltaAdjusted?.value) ? isDeltaAdjusted.value : undefined;
      return validateHedgingTradeBackToBackFields(
        fileCode,
        isDefined(isDeltaAdjustedValue) ? { value: isDeltaAdjustedValue } : undefined,
        noteNominal,
        isDelayedBackToBack,
      );
    }
    default:
      return [];
  }
}

export function getFormQuoteResult(state: AppState, workspace: WorkspaceType, tabId: string) {
  const quoteId = getFormQuoteId(state, workspace, tabId);
  return getQuoteResultFromQuoteId(state, quoteId);
}
export function getFormQuoteDetails(
  state: AppState,
  workspace: WorkspaceType,
  tabId: string,
): QuoteDetailsData | undefined {
  const requestType = getFormRequestType(state, workspace, tabId);
  if (requestType === 'RequestForStream') {
    return getStreamingDetails(state, tabId);
  }
  switch (workspace) {
    case 'static':
      return getStaticQuoteDetails(state, tabId);
    case 'dynamic':
      return getDynamicQuoteDetails(state, tabId);
    case 'postTrade':
      return addonSelector.getQuoteDetails(state, tabId);
    case 'newBatch':
      return getBatchQuoteDetails(state, tabId);
    case 'essential': {
      const quoteId = selectEssentialsFormQuoteId(state, tabId);
      return quoteId ? selectQuoteDetails(state, quoteId) : undefined;
    }
    default:
      return undefined;
  }
}

export function getPricerRfqIdByWorkspaceTab(state: AppState, workspaceType: QuoteWorkspaceType, tabId: string) {
  return getFormQuoteDetails(state, workspaceType, tabId)?.PricerRfqId;
}

export function getQuoteCurrency(state: AppState, workspace: WorkspaceType, tabId: string) {
  return getFormQuoteDetails(state, workspace, tabId)?.Currency;
}

export function getQuoteNotional(state: AppState, workspace: WorkspaceType, tabId: string) {
  return getFormQuoteDetails(state, workspace, tabId)?.NotionalAmount;
}
export function getQuotePricerToBookingInvertedFlow(state: AppState, workspace: WorkspaceType, tabId: string): boolean {
  return getFormQuoteDetails(state, workspace, tabId)?.PayoffSpecificities?.pricerToBookingInvertedFlow ?? false;
}
export function getQuoteFees(state: AppState, workspace: WorkspaceType, tabId: string) {
  const quoteDetails = getFormQuoteDetails(state, workspace, tabId);
  return quoteDetails?.Fees?.filter(fee => !fee.name.includes('http'));
}
export function getQuoteFeesDetailLink(state: AppState, workspace: WorkspaceType, tabId: string) {
  const quoteDetails = getFormQuoteDetails(state, workspace, tabId);
  return quoteDetails?.Fees?.find(fee => fee.name.includes('http'))?.name;
}

export function getFormQuoteFxSpot(state: AppState, workspaceType: WorkspaceType, tabId: string): GreekLine | null {
  const quoteDetails = getFormQuoteDetails(state, workspaceType, tabId);
  if (quoteDetails?.MustDisplayFxSpot !== true) {
    return null;
  }
  const spots = quoteDetails.Greeks?.cells.filter(x => x.name === 'Spot') ?? [];
  const underlying = quoteDetails.Underlyings?.[0]?.name;
  const matching = spots.find(x => x.reference === underlying);
  return matching ?? null;
}

export function getQuotePricerId(state: AppState, workspace: WorkspaceType, tabId: string): PricerId | undefined {
  const quoteDetails = getFormQuoteDetails(state, workspace, tabId);
  return quoteDetails?.PricerRfqId;
}

export function getFormQuoteYield(state: AppState, workspace: WorkspaceType, tabId: string): string | undefined {
  return getFormQuoteDetails(state, workspace, tabId)?.Yield;
}

export function getQuoteResultFromQuoteId(state: AppState, quoteId: string | undefined) {
  return fromQuoteResult.getQuoteResult(state.quotes.results, quoteId);
}
export function getFormQuoteRejections(state: AppState, workspace: QuoteWorkspaceType, tabId: string) {
  const quoteId = getFormQuoteId(state, workspace, tabId);
  const requestType = getFormRequestType(state, workspace, tabId);
  if (requestType === 'RequestForStream') {
    return getRejections(state, tabId);
  }
  return getQuoteRejectionsByQuoteId(state, quoteId);
}

export function getQuoteRejectionsByQuoteId(state: AppState, quoteId: string | undefined) {
  const quoteResult = fromQuoteResult.getQuoteResult(state.quotes.results, quoteId);
  const tradeInfos = fromTrade.getTradeInfos(state.quotes.trades, quoteId);
  return tradeInfos?.tradeRejections ?? quoteResult?.rejections;
}

export function getQuoteWarningsByQuoteId(state: AppState, quoteId: string | undefined) {
  const quoteResult = fromQuoteResult.getQuoteResult(state.quotes.results, quoteId);
  return quoteResult?.warnings;
}

export function getFormWorkflowStatus(state: AppState, workspace: QuoteWorkspaceType, tabId: string) {
  return workspace === 'static' ? getStaticWorkflowStatus(state, tabId) : getDynamicWorkflowStatus(state, tabId);
}

export function getFormWorkflowStatusQuoted(state: AppState, workspace: QuoteWorkspaceType, tabId: string) {
  const status = getFormWorkflowStatus(state, workspace, tabId);
  return status === 'QUOTED' || status === 'QUOTED_TRADABLE' || status === 'QUOTED_PREVIOUSLY';
}

export function getPriceEnhancementMode(state: AppState, workspace: QuoteWorkspaceType, tabId: string) {
  return workspace === 'static'
    ? getStaticFormPriceEnhancementMode(state, tabId)
    : getDynamicFormPriceEnhancementMode(state, tabId);
}

export function getQuoteUnderlyings(state: AppState, tabId: string): Underlying[] | undefined {
  const values = getFormEnrichedData(state, tabId);
  return values?.Underlyings as Underlying[];
}

export function getQuoteSolvingMode(state: AppState, tabId: string) {
  const solvingModeValue = getFormEnrichedData(state, tabId)?.SolvingMode as string;
  const translationKey = getSolvingModeTranslationKeyForStaticScreenOnly(state, tabId);

  return translationKey ?? solvingModeValue;
}

export function getFixedAmountInPercent(state: AppState, tabId: string) {
  return getFormEnrichedData(state, tabId)?.FixedAmountInPercent as number;
}

export function getQuoteRemunerationMode(state: AppState, tabId: string) {
  return getFormEnrichedData(state, tabId)?.RemunerationMode as string;
}

export function getQuotePriceInCurrency(state: AppState, workspace: WorkspaceType, tabId: string) {
  const priceInPercent = getQuotePriceInPercent(state, tabId);
  const notionalAmount = getQuoteNotional(state, workspace, tabId) ?? 0;
  return (notionalAmount * priceInPercent) / 100;
}

export function getQuotePriceInPercent(state: AppState, tabId: string) {
  const remunerationMode = getQuoteRemunerationMode(state, tabId);
  if (remunerationMode === 'Reoffer') {
    return (getFormEnrichedData(state, tabId)?.OfferPrice as number) ?? 0;
  }

  if (remunerationMode === 'Upfront') {
    return (getFormEnrichedData(state, tabId)?.UpfrontFee as number) ?? 0;
  }

  if (remunerationMode === 'Premium') {
    return (getFormEnrichedData(state, tabId)?.FixedAmountInPercent as number) ?? 0;
  }
  return 0;
}

export function getQuoteSolvingModeValue(state: AppState, tabId: string) {
  const solvingModeValue = getFormEnrichedData(state, tabId)?.SolvingMode as string;
  return solvingModeValue;
}

export function getSolvingModeTranslationKeyForStaticScreenOnly(state: AppState, tabId: string) {
  const solvingModeValue = getFormEnrichedData(state, tabId)?.SolvingMode as string;
  const fieldsMetadata = getFieldsMetadata(state, 'static', tabId);
  if (!fieldsMetadata) {
    return solvingModeValue;
  }

  const solvingModeMetadata = fieldsMetadata.SolvingMode as ListFieldMetadata;
  if (!solvingModeMetadata) {
    return solvingModeValue;
  }

  const solvingModeValueMetadata = solvingModeMetadata.availableItems.find(
    availableItem => availableItem.value === solvingModeValue,
  );

  return solvingModeValueMetadata?.translationKey;
}

export function getFormProductType(state: AppState, tabId: string) {
  const values = getFormEnrichedData(state, tabId);
  return values?.ProductType as string;
}

export function getFormProductSubType(state: AppState, tabId: string) {
  const values = getFormEnrichedData(state, tabId);
  return values?.ProductSubtype as string;
}
export function getFormProduct(state: AppState, tabId: string) {
  const productType = getFormProductType(state, tabId);
  const productSubtype = getFormProductSubType(state, tabId);
  if (isDefined(productType) && isDefined(productSubtype)) {
    return { productType, productSubtype };
  }
}
export function getQuoteClientId(state: AppState, tabId: string) {
  const values = getFormEnrichedData(state, tabId);
  const client: ISalesCounterpart = values?.SelectedOnBehalfClient as ISalesCounterpart;
  return client ? client.id : getUserCompanyBdrId(state);
}

export function getFormIsEnrichmentError(state: AppState, workspace: QuoteWorkspaceType, tabId: string): boolean {
  return workspace === 'static'
    ? getStaticFormIsEnrichmentError(state.static, tabId)
    : getDynamicFormIsEnrichmentError(state.dynamic, tabId);
}

const emptyErrors: readonly [] = [];

export function getSidePanelValidationErrors(
  state: AppState,
  workspaceType: WorkspaceType,
  formId: string,
): readonly QuoteFormError[] {
  switch (workspaceType) {
    case 'static':
      return getStaticFormInfos(state, formId)!.validationErrors ?? emptyErrors;
    case 'dynamic':
      return getDynamicFormInfos(state, formId)!.validationErrors ?? emptyErrors;
    default:
      return emptyErrors;
  }
}

export function enrichPatchWithAutoSolve(
  state: AppState,
  workspace: WorkspaceType,
  _tabId: string,
  formId: string,
  patch: DataById,
): DataById {
  if (workspace === 'newBatch') {
    const solveBy = getBatchAutoSolve(state, formId, patch);
    return solveBy ? { ...patch, SolvingMode: solveBy } : patch;
  }

  return patch;
}

export function canRemoveQuoteData(
  state: AppState,
  { removedTabId, quoteId }: { removedTabId: string; quoteId: string },
) {
  const stillUsedTabIds = getTabIdsByQuoteId(state, quoteId)?.filter(t => t !== removedTabId);
  if (stillUsedTabIds.length > 0) {
    return false;
  }

  const dashboardQuoteIds = getAllQuoteIdsUsedInDashboard(state);
  if (dashboardQuoteIds.includes(quoteId)) {
    return false;
  }
  return true;
}

export function isQuoteConsistentForAdvisory(state: AppState, workspace: QuoteWorkspaceType, tabId: string): boolean {
  const quoteState = getFormQuoteState(state, workspace, tabId);

  switch (quoteState) {
    case 'Inconsistent':
      return false;
    case 'Previous': {
      const pricingDate = getFormQuoteResult(state, workspace, tabId)?.createdAt;
      return !!pricingDate && getDateOnlyFromIsoString(pricingDate) === getDateOnlyFromDate(now());
    }
    default:
      return true;
  }
}

export function getQuoteTypeByAvailableActions(
  state: AppState,
  workspace: 'static' | 'dynamic',
  tabId: string,
): QuoteType | undefined {
  const quoteId = getFormQuoteId(state, workspace, tabId);
  if (!isDefined(quoteId)) {
    return undefined;
  }
  const quoteType = state.quotes.infos[quoteId].quoteType;
  if (!isDefined(quoteType)) {
    return undefined;
  }
  const formInfo = getFormInfos(state, workspace, tabId);
  const availableActions = formInfo?.actions;
  const tradaActionExist =
    availableActions !== undefined && availableActions.some(action => action.name === 'TradableQuote');
  if (!tradaActionExist) {
    switch (quoteType) {
      case 'Tradable':
        return 'Executable';
      case 'AdvancedTradable':
        return 'AdvancedExecutable';
      default:
        return quoteType;
    }
  }

  return quoteType;
}

export function hasAddonAction(state: AppState, tabId: string, workspaceType: 'static' | 'dynamic') {
  return getFormQuoteResult(state, workspaceType, tabId)?.isExecOnlyAddon ?? false;
}
